/**
 * @file types.ts
 * @description Тип заготовки секции и группы библиотеки «Добавить блок»
 * (phase19.16 Р2; SB27; uiux-design-r2 §5; db-design-r2 §8).
 *
 * Заготовки платформы — код (версионируются вместе со схемой, в бэкап не
 * попадают). Свои заготовки магазина — Р3 (SB28), отдельной моделью.
 *
 * Р2а (db-design-r2a; uiux-design-r2a §3): у заготовки есть контексты, где её
 * можно вставить (`contexts`), и группа «Страница товара» — раскладки шаблона
 * страницы товара (только контекст productTemplate).
 */

import type { SectionContext } from "../contexts.js";
import type { SectionPayload } from "../schema.js";

/** Группы библиотеки в порядке показа (левая колонка окна «Добавить блок»). */
export const SECTION_PRESET_GROUPS = [
  // Р2а: видна только в шаблоне страницы товара — там она первая.
  { key: "product", label: "Страница товара" },
  { key: "hero", label: "Первый экран" },
  { key: "tiles", label: "Плитки" },
  { key: "products", label: "Товары" },
  { key: "text-photo", label: "Текст и фото" },
  { key: "cta", label: "Призыв" },
  { key: "service", label: "Сервис" },
  { key: "reviews", label: "Отзывы" },
  { key: "faq", label: "Вопросы" },
  { key: "contacts", label: "Контакты" },
  { key: "newsletter", label: "Подписка" },
  { key: "blog", label: "Блог" },
  { key: "empty", label: "Пустой блок" },
] as const;

export type SectionPresetGroup = (typeof SECTION_PRESET_GROUPS)[number]["key"];

export interface SectionPreset {
  /** Стабильный ключ (поиск, превью, аналитика). */
  readonly key: string;
  readonly group: SectionPresetGroup;
  /** Название карточки: «Фото справа в край». */
  readonly title: string;
  /** «Для чего» — строка под названием и материал для поиска. */
  readonly hint: string;
  /** Ключ SVG-схемы превью в кабинете (окрашивается ролями магазина). */
  readonly previewKey: string;
  /**
   * Фон секции — фото: кабинет после вставки сразу предлагает медиатеку
   * (фон-фото без файла не бывает, поэтому заготовка приходит с цветом роли).
   */
  readonly backgroundPhoto: boolean;
  /** SB26: показывать только при работающей рассылке магазина. */
  readonly requires: "newsletter" | null;
  /**
   * Р2а: где заготовку можно вставить. Общие — везде; раскладки страницы
   * товара — только в шаблоне (в них элементы данных товара).
   */
  readonly contexts: readonly SectionContext[];
  /** Новый payload (каждый вызов — новый объект). */
  build(): SectionPayload;
  /**
   * Только у раскладок шаблона страницы товара (группа `product`): все блоки
   * раскладки. Применение раскладки заменяет блоки черновика шаблона целиком
   * (как «Сбросить к стандартному»), потому что элементы данных товара в
   * шаблоне не повторяются. `build()` у раскладки — её главный блок (с «Ценой
   * и покупкой»), для превью; вставлять его вторым блоком в шаблон, где уже
   * есть элементы товара, нельзя — запись отклонит `checkProductTemplate`.
   */
  buildLayout?(): SectionPayload[];
}

/** Контексты общих заготовок: обычная страница, места и шаблон товара (общие элементы разрешены везде). */
export const GENERAL_PRESET_CONTEXTS: readonly SectionContext[] = ["page", "slot", "productTemplate"];
